<script setup>
import { computed, ref, watch } from "vue";
import { useCouponStore } from "@/stores/useCouponStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import { toTitleCase } from "@/utils/stringUtils";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  couponId: { type: Number, required: true },
});

const emit = defineEmits(["close"]);

const couponStore = useCouponStore();
const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

const coupon = computed(() => {
  if (
    couponStore.selectedCoupon &&
    couponStore.selectedCoupon.couponId === props.couponId
  ) {
    return couponStore.selectedCoupon;
  }

  return couponStore.coupons.find((c) => c.couponId === props.couponId) || null;
});

watch(
  () => props.couponId,
  async (newId) => {
    if (!newId) return;

    isLoadingData.value = true;

    try {
      await couponStore.getCouponById(newId);
    } catch (e) {
      console.error("Erro ao carregar coupon no CardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

const discountLabel = computed(() => {
  if (!coupon.value) return "0%";
  return `${coupon.value.discountPercent ?? 0}%`;
});

const redemptionsLabel = computed(() => {
  if (!coupon.value) return "0 / ∞";

  const used = Number(coupon.value.redemptionsCount ?? 0);
  const max = coupon.value.maxRedemptions;

  if (max === null || max === undefined || max === "") {
    return `${used} / ∞`;
  }

  return `${used} / ${max}`;
});

const cyclesLabel = computed(() => {
  if (!coupon.value) return "Apenas 1º ciclo";
  return coupon.value.applyToAllCycles
    ? "Todos os ciclos"
    : "Apenas 1º ciclo";
});

const statusLabel = computed(() => {
  if (!coupon.value) return "Inativo";
  return coupon.value.isActive ? "Ativo" : "Inativo";
});

const isExpired = computed(() => {
  if (!coupon.value?.expiresAt) return false;

  const expiresAt = new Date(coupon.value.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return false;

  return expiresAt.getTime() < Date.now();
});

const expirationLabel = computed(() => {
  if (!coupon.value?.expiresAt) return "Não informado";
  return formatDateTimeForDisplay(coupon.value.expiresAt);
});
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="coupon-card-modal-content" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">Detalhes do Coupon</h3>
        <button
          @click="emit('close')"
          type="button"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div v-if="coupon && !isLoadingData" class="card-content-grid">
        <div class="details-col">
          <div class="title-row">
            <h3 class="text-title-bold">
              <span class="coupon-code" :title="coupon.code">
                {{ coupon.code }}
              </span>
              <span class="title-id">(Id: {{ coupon.couponId }})</span>
            </h3>
            <span class="discount-badge">{{ discountLabel }}</span>
          </div>

          <div class="meta-grid">
            <div class="meta-item">
              <span class="meta-label">Regra</span>
              <span class="meta-value">
                {{ toTitleCase(coupon.eligibleRule ?? "") }}
              </span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Ciclos</span>
              <span class="meta-value">{{ cyclesLabel }}</span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Resgates</span>
              <span class="meta-value">{{ redemptionsLabel }}</span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Máximo de Resgates</span>
              <span class="meta-value">
                {{ coupon.maxRedemptions ?? "Ilimitado" }}
              </span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Expira em</span>
              <span
                class="meta-value"
                :class="{ 'expired-value': isExpired }"
              >
                {{ expirationLabel }}
              </span>
            </div>

            <div class="meta-item">
              <span class="meta-label">Status</span>
              <span class="meta-value">{{ statusLabel }}</span>
            </div>
          </div>

          <div class="features-row">
            <div
              :class="['feature-tag', coupon.isActive ? 'active' : 'inactive']"
            >
              <i
                :class="[
 'fas',
                  coupon.isActive ? 'fa-check-circle' : 'fa-times-circle',
                ]"
              ></i>
              Ativo
            </div>

            <div
              :class="[
 'feature-tag',
                coupon.applyToAllCycles ? 'active' : 'inactive',
              ]"
            >
              <i
                :class="[
 'fas',
                  coupon.applyToAllCycles
                    ? 'fa-check-circle'
                    : 'fa-times-circle',
                ]"
              ></i>
              Todos os ciclos
            </div>

            <div :class="['feature-tag', !isExpired ? 'active' : 'inactive']">
              <i
                :class="[
 'fas',
                  !isExpired ? 'fa-check-circle' : 'fa-times-circle',
                ]"
              ></i>
              {{ isExpired ? "Expirado" : "Vigente" }}
            </div>
          </div>

          <hr class="content-separator" />

          <div class="footer-info">
            <div class="users-row">
              <div class="user-chip">
                <div class="chip-text">
                  <span class="chip-label">Criação</span>
                  <span class="chip-date">
                    {{ formatDateTimeForDisplay(coupon.createdAt) }}
                  </span>
                </div>
              </div>

              <div class="user-chip">
                <div class="chip-text">
                  <span class="chip-label">Última Atualização</span>
                  <span class="chip-date">
                    {{ formatDateTimeForDisplay(coupon.updatedAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes do coupon...
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

.coupon-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 650px;
  position: relative;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
}

/* Os botões do cabeçalho continuam sendo clique, não alça */
.modal-header--draggable .modal-close-btn,
.modal-header--draggable .modal-header-actions button {
  cursor: pointer;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.card-content-grid {
  padding: 1rem;
}

.details-col {
  width: 100%;
}

.title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.text-title-bold {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-dark);
  line-height: 1.25;
  min-width: 0;
}

.coupon-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-weight: 700;
  letter-spacing: 0.02em;
  word-break: break-word;
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.discount-badge {
  background: var(--success-bg);
  color: var(--success-text);
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.95rem;
  white-space: nowrap;
}

.meta-grid {
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

.expired-value {
  color: #c92a2a;
  font-weight: 700;
}

.features-row {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  margin-top: 6px;
  margin-bottom: 6px;
  overflow-x: auto;
}

.feature-tag {
  padding: 0.2rem 0.45rem;
  white-space: nowrap;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.feature-tag.active {
  background: var(--brand-soft);
  color: #3730a3;
}

.feature-tag.inactive {
  background: var(--neutral-100);
  color: var(--text-subtle);
  text-decoration: line-through;
}

.footer-info {
  padding-top: 8px;
  width: 100%;
}

.users-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
}

</style>
